import {
  WORKSPACE_NAVIGATION_ENTRIES, ADMIN_NAVIGATION_ENTRIES, adminTabUrl, workspaceUrl,
  type NavigationAccessInput, type NavigationContextRequirement, type NavigationLabelContext,
  type WorkspaceNavigationEntry, type AdminNavigationEntry,
} from '../components/workspaces/admin/navigationRegistry'
import { adminNavigationForExperience, adminNavigationLabel } from '../components/workspaces/admin/adminNavigationPresentation'
import { describeMarketDemandCapability, resolveMarketDemandCapabilities } from '../hooks/useMarketDemandCapabilities'
import type { CommandContext, CommandDefinition } from './commandRegistry'
import { navigateTo } from './navigation'

function contextReady(requirement: NavigationContextRequirement, input: NavigationAccessInput): boolean {
  const access = input.accessContext
  if (!access?.currentUser?.id || (input.selectedTenantId && input.selectedTenantId !== access.tenantId)) return false
  if (requirement === 'none') return true
  if (!access.tenantId) return false
  if (requirement === 'tenant') return true
  const organization = input.currentOrganization
  return Boolean(organization && (!organization.tenant_id || organization.tenant_id === access.tenantId)
    && access.visibleOrganizations?.some(item => item.id === organization.id))
}

function workspaceEligible(entry: WorkspaceNavigationEntry, input: NavigationAccessInput): boolean {
  // The shell explicitly supports tenantless Mentor Admin, bypassing its tenant billing gate.
  const requirement = entry.key === 'mentorship-admin' && !input.accessContext?.tenantId ? 'none' : entry.contextRequirement
  if (!WORKSPACE_NAVIGATION_ENTRIES.includes(entry) || !contextReady(requirement, input) || !entry.isVisible(input) || entry.isDisabled?.(input)) return false
  if (entry.billingFeatureKey && input.accessContext?.tenantId
    && input.accessContext.billingEntitlements?.features?.[entry.billingFeatureKey]?.enabled !== true) return false
  if (entry.key === 'job-bank' || entry.key === 'market-opportunities') {
    return describeMarketDemandCapability(resolveMarketDemandCapabilities(input.accessContext), entry.feature).enabled
  }
  return true
}

function adminEligible(entry: AdminNavigationEntry, context: CommandContext): boolean {
  const navigation = context.navigation
  const admin = WORKSPACE_NAVIGATION_ENTRIES.find(item => item.key === 'admin')
  if (!navigation || !admin || !workspaceEligible(admin, navigation.input)
    || !contextReady(entry.contextRequirement, navigation.input)) return false
  return adminNavigationForExperience(navigation.input, navigation.isSimple).includes(entry)
    && (!entry.billingFeatureKey || navigation.input.accessContext?.billingEntitlements?.features?.[entry.billingFeatureKey]?.enabled === true)
}

/** Convert the existing switcher registry; never derive authority from route or feature name. */
export function workspaceNavigationCommands(context: CommandContext, labels: NavigationLabelContext): CommandDefinition[] {
  if (!context.navigation) return []
  return WORKSPACE_NAVIGATION_ENTRIES.map(entry => ({
    id: `navigation.workspace.${entry.key}`,
    remember: true,
    label: labels.t(entry.resolveLabel(labels)),
    description: labels.t(entry.resolveDescription(labels)),
    category: 'Workspaces',
    keywords: [entry.resolveLabel({ t: key => key }), entry.cta],
    destination: { route: entry.route, workspace: entry.key },
    available: (current: CommandContext) => Boolean(current.navigation && workspaceEligible(entry, current.navigation.input)),
    execute: () => navigateTo(workspaceUrl(entry.key)),
  })).filter(command => command.available(context))
}

/** Admin permission arrays retain their registry's any-of semantics, never command all-of. */
export function adminNavigationCommands(context: CommandContext, labels: NavigationLabelContext): CommandDefinition[] {
  if (!context.navigation) return []
  return ADMIN_NAVIGATION_ENTRIES.map(entry => ({
    id: `navigation.admin.${entry.key}`,
    remember: true,
    label: labels.t(adminNavigationLabel(entry.key, entry.resolveLabel(labels), { ...labels, isSimple: context.navigation!.isSimple })),
    description: labels.t(entry.resolveDescription?.(labels) || 'Admin workspace'),
    category: 'Admin workspace',
    keywords: [entry.resolveLabel({ t: key => key }), ...(entry.aliases || [])],
    destination: { route: entry.route, workspace: 'admin' as const },
    available: (current: CommandContext) => adminEligible(entry, current),
    execute: () => navigateTo(adminTabUrl(entry.key, '/admin')),
  })).filter(command => command.available(context))
}
